@file:UseSerializers(BigDecimalSerializer::class)
@file:Suppress("KDocUnresolvedReference")

package net.sergeych.ugdx.shared.api

import com.ionspin.kotlin.bignum.decimal.BigDecimal
import com.ionspin.kotlin.bignum.decimal.DecimalMode
import com.ionspin.kotlin.bignum.decimal.RoundingMode
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.serialization.Serializable
import kotlinx.serialization.UseSerializers
import net.sergeych.kiloparsec.Command
import net.sergeych.kiloparsec.command
import net.sergeych.mptools.CachedExpression
import net.sergeych.mptools.bd
import net.sergeych.ugdx.GuestAPI
import net.sergeych.ugdx.shared.ApiOperation
import net.sergeych.ugdx.shared.BigDecimalSerializer
import net.sergeych.ugdx.shared.Currency
import net.sergeych.ugdx.shared.trader.CurrencyPair
import kotlin.time.Duration.Companion.hours

@Serializable
data class ApiTestImportArgs(val currencyCode: String, val amount: BigDecimal)

val cmdTestImport by command<ApiTestImportArgs, Unit>()

suspend operator fun <A,R>Command<A,R>.invoke(args: A): R =
    GuestAPI().call(this, args)

suspend operator fun <R>Command<Unit,R>.invoke(): R =
    GuestAPI().call(this, Unit)

@Serializable
data class ApiAccount(
    val id: Long,
    val balance: BigDecimal,
    val currencyCode: String,
    val name: String?,
    val serial: Long,
    val updatedAt: Instant,
    val isDefault: Boolean,
    val isBrokerage: Boolean,
) {

    /**
     * Get list of operations in a time period
     * @param since inclusive
     * @param until exclusive
     * @return list of operations for the period
     */
    suspend fun operations(since: Instant, until: Instant): List<ApiOperation> =
        cmdGetAccountOperations(AccountOperationsArgs(id, since, until)).operations

    /**
     * Update account parameters to new values
     */
    suspend fun update(name: String, isDefault: Boolean, isBrokerage: Boolean): ApiAccount =
        GuestAPI().userAccountOrThrow.updateAccount(this, name, isDefault, isBrokerage)

    suspend fun refresh() = GuestAPI().userAccountOrThrow.refreshAccount(this)

    suspend fun lastOperations(): List<ApiOperation> {
        val now = Clock.System.now()
        return operations(now - 12.hours, now)
    }

    /**
     * Get the address (in string form) to which send funds to improt into thsi account. null means import is
     * not available at this time. Exception with [ErrorCode.INVALID_OPERATION] is thrown is account does not permit
     * import operations.
     */
    suspend fun getImportAddress(): String? {
        return cmdGetImportAddress(GetImportAddressArgs(id)).address
    }

    /**
     * __Experimental__ calculate amount of funds that shoult be frozen on thos balance to fulfill active [TradeOrders]
     */
    suspend fun getFrozenBalance(): BigDecimal {
        var result = 0.bd
        val scale = CachedExpression<Long>()
        CurrencyPair.allOrders(true).collect { order ->
            order.limit?.let { rate ->
                // There could be 2 relevant situations:
                if (order.sellAccountId == id) {
                    if (order.isBuy) {
                        // this account is used to pay for buy order
                        result += order.amount * rate
                    } else {
                        // I sell from this account
                        val s = scale.get { GuestAPI().currencyWithCode(currencyCode).roundingScale.toLong() }
                        result += order.amount.divide(rate, DecimalMode(s, RoundingMode.ROUND_HALF_TOWARDS_ZERO))
                    }
                }
            }
        }
        return result
    }

    /**
     * Estimate the "free to use" balance, e.g. minus all funds that _should be_ frozen for current active operations.
     * Note that these funds are not actually frozen by th service, but lack of funds _may_ cause some trade orders to
     * be automatically rejected:
     */
    suspend fun getAvailableBalance(): BigDecimal = balance - getFrozenBalance()

    override fun toString(): String =
        if( name.isNullOrBlank() )
            "#$id $currencyCode"
        else
            name
}

@Serializable
data class GetAccountsResult(val accounts: List<ApiAccount>) {
    /**
     * Get the list of accounts for a given currency sorted in a way that default account will be first (either the
     * defaul, where `isDefault == true`, otherwise oldest first (ugdx consider oldest account as default unless
     * explicitly specified). So the default account is `byCurrency(currency).first()`
     */
    fun byCurrency(currency: Currency) =
        accounts.filter { it.currencyCode == currency.code }.sortedWith { a, b ->
            if (a.isDefault != b.isDefault)
                if (a.isDefault) -1 else +1
            else
                a.id.compareTo(b.id)
        }
}

val cmdGetAccounts by command<Unit, GetAccountsResult>()

@Serializable
data class CreateAccountArgs(
    val currencyCode: String,
    val name: String,
)

val cmdCreateAccount by command<CreateAccountArgs, ApiAccount>()

@Serializable
data class LocalTransferArgs(val fromAccountId: Long, val toAccountId: Long, val amount: BigDecimal)

val cmdTransferToMyAccount by command<LocalTransferArgs,Unit>()

//@Serializable
//data class TransferArgs(val fromAccountId: Long, val toLoginName: String, val amount: BigDecimal, val purpose: String)

//val cmdTransferToLoginName by command<TransferArgs, EmptyResult>("Accounts.transferToLoginName")


@Serializable
data class AccountIdArgs(val accountId: Long)

val cmdDeleteAccount by command<AccountIdArgs,Unit>()

@Serializable
data class AccountOperationsArgs(val accountId: Long, val since: Instant, val until: Instant)

@Serializable
data class AccountOperationsResult(val operations: List<ApiOperation>)

val cmdGetAccountOperations by command<AccountOperationsArgs, AccountOperationsResult>()

@Serializable
data class UpdateAccountArgs(val accountId: Long, val isDefault: Boolean, val isBrokerage: Boolean, val name: String)

val cmdUpdateAccount by command<UpdateAccountArgs,ApiAccount>()

@Serializable
data class GetImportAddressArgs(val accountId: Long)

@Serializable
data class GetImportAddressResult(val accountId: Long, val address: String?)

val cmdGetImportAddress by command<GetImportAddressArgs, GetImportAddressResult>()

@Serializable
data class GetRegisteredImportAddressesResult(val addresses: List<String>)

val cmdGetRegisteredImportAddresses by command<Unit,GetRegisteredImportAddressesResult>()
